import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import { isComplaintTask, assigneeOptionsFor, assigneeOptionLabel, COMPLAINT_ASSIGNEE_ERROR_CODE, COMPLAINT_ASSIGNEE_ERROR_MESSAGE } from "../../utils/complaintAssignee";

// Phase 4A, §21. Reassignment changes OFFICIAL ownership (tasks.assigned_to)
// — kept a deliberately separate, small dialog from performer/delegation
// management so the "this changes accountability" warning is never missed
// among unrelated controls.
// `task` (optional): { task_type, task_type_code, assigned_to, assigned_to_name }
// -- for an IT Support complaint only Technicians (plus the current owner)
// are offered, the rule POST /tasks/:id/reassign enforces. Without it the
// list is unchanged (the backend still enforces).
export default function ReassignTaskDialog({ show, onHide, taskId, currentAssigneeName, onReassigned, task }) {
  const [users, setUsers] = useState([]);
  const [toAssigneeId, setToAssigneeId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setToAssigneeId("");
    setReason("");
    api.get("/users").then((res) => setUsers(Array.isArray(res.data) ? res.data : [])).catch(() => setUsers([]));
  }, [show]);

  const complaint = task ? isComplaintTask(task) : false;
  const options = task ? assigneeOptionsFor(task, users) : users;

  const submit = async () => {
    if (!toAssigneeId) {
      toast.warning("Select a new assignee");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/reassign`, { to_assignee_id: Number(toAssigneeId), reason: reason || undefined });
      toast.success("Task reassigned");
      onReassigned?.();
      onHide();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_ALREADY_ASSIGNED_TO_USER") toast.error("This task is already assigned to that user.");
      else if (data?.code === "TASK_PROJECT_MEMBER_REQUIRED") toast.error("The selected user must be an active member of this task's project/module.");
      else if (data?.code === "TASK_REASSIGNMENT_NOT_ALLOWED") toast.error("You do not have permission to reassign this task.");
      else if (data?.code === COMPLAINT_ASSIGNEE_ERROR_CODE) toast.error(data?.message || data?.error || COMPLAINT_ASSIGNEE_ERROR_MESSAGE);
      else toast.error(data?.message || "Failed to reassign task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Reassign Task</Modal.Title></Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="small">
          Reassignment changes official accountability. Existing performer and delegation history will remain.
        </Alert>
        {currentAssigneeName && <div className="small text-muted mb-2">Currently assigned to: <strong>{currentAssigneeName}</strong></div>}
        <Form.Label className="small" htmlFor="reassign-new-assignee">{complaint ? "Assign Technician" : "New Assignee"}</Form.Label>
        <Form.Select id="reassign-new-assignee" value={toAssigneeId} onChange={(e) => setToAssigneeId(e.target.value)}>
          <option value="">{complaint ? "Select Technician..." : "Select..."}</option>
          {options.map((u) => <option key={u.id} value={u.id}>{assigneeOptionLabel(u)}</option>)}
        </Form.Select>
        <Form.Label className="small mt-3">Reason (optional)</Form.Label>
        <Form.Control as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" disabled={saving} onClick={submit}>{saving ? "Reassigning..." : "Reassign"}</Button>
      </Modal.Footer>
    </Modal>
  );
}
